// InteractionTracker.jsx
// Wrap this ONCE around all client-facing content (not per-section - SectionTracker
// already handles per-section wrapping, and tags each section with a
// data-track-section attribute that this component reads to know which section
// a click/hover happened inside).
//
// Usage - wrap the whole routed content, e.g. in App.js's Layout:
//   <InteractionTracker>
//     <AppRoutes />
//   </InteractionTracker>
//
// What gets tracked automatically: <button>, <a>, <img>, <video>, and anything
// with an explicit data-track="your-label-here" attribute. To label an element
// yourself (recommended for anything where the auto-derived label would be vague,
// e.g. an icon-only button), just add data-track="download-brochure" etc. - no
// other wiring needed, InteractionTracker will pick it up automatically since it's
// a single delegated listener, not something you register per element.

import React, { useEffect, useRef } from "react";
import { enqueueInteraction } from "./interactionEventQueue";

const TRACKABLE_SELECTOR = "button, a, img, video, [data-track]";
const MIN_HOVER_MS = 400; // ignore accidental fly-overs shorter than this

function getElementLabel(el) {
  if (el.dataset.track) return el.dataset.track;

  if (el.tagName === "IMG") {
    return el.alt || el.src?.split("/").pop() || "unlabeled-image";
  }
  if (el.tagName === "A" || el.tagName === "BUTTON") {
    const text = el.textContent?.trim();
    if (text) return text.slice(0, 40);
  }
  return el.tagName.toLowerCase();
}

function getSectionName(el) {
  const sectionEl = el.closest("[data-track-section]");
  return sectionEl ? sectionEl.dataset.trackSection : "unknown-section";
}

export default function InteractionTracker({ children }) {
  const containerRef = useRef(null);
  const hoverStartMap = useRef(new Map()); // element -> Date entered

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    function handleClick(e) {
      const el = e.target.closest(TRACKABLE_SELECTOR);
      if (!el) return;

      enqueueInteraction({
        sectionName: getSectionName(el),
        elementLabel: getElementLabel(el),
        interactionType: "CLICK",
        occurredAt: new Date().toISOString(),
      });
    }

    function handleMouseOver(e) {
      const el = e.target.closest(TRACKABLE_SELECTOR);
      if (!el) return;
      // Only fire when genuinely entering this element, not moving between its own children
      if (e.relatedTarget && el.contains(e.relatedTarget)) return;

      hoverStartMap.current.set(el, new Date());
    }

    function handleMouseOut(e) {
      const el = e.target.closest(TRACKABLE_SELECTOR);
      if (!el) return;
      if (e.relatedTarget && el.contains(e.relatedTarget)) return;

      const start = hoverStartMap.current.get(el);
      if (!start) return;
      hoverStartMap.current.delete(el);

      const durationMs = new Date() - start;
      if (durationMs < MIN_HOVER_MS) return; // too brief to count as real interest

      enqueueInteraction({
        sectionName: getSectionName(el),
        elementLabel: getElementLabel(el),
        interactionType: "HOVER",
        occurredAt: new Date().toISOString(),
        durationSeconds: Math.round(durationMs / 1000),
      });
    }

    container.addEventListener("click", handleClick);
    container.addEventListener("mouseover", handleMouseOver);
    container.addEventListener("mouseout", handleMouseOut);

    return () => {
      container.removeEventListener("click", handleClick);
      container.removeEventListener("mouseover", handleMouseOver);
      container.removeEventListener("mouseout", handleMouseOut);
    };
  }, []);

  return <div ref={containerRef}>{children}</div>;
}